import { getMessageWithPlaceholders } from 'shared-locales';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { pathCanBeJoined } from 'lib/paths';
import {
  ApiDownloadButton,
  Badge,
  ContentLabel,
  ContentValue,
  FlexContainer,
  PublishedProductText,
  RichTextValue,
  User,
  UserPathStepProgress
} from 'components/common';
import { Box } from 'components/common/box';
import { DisplayAttributeInProductPage } from 'components/common/customAttributes';
import { CompetenciesProductBadge } from 'components/competencies';
import { PathPicture, PathsTileJoinPath } from '.';

import type { DetailedPath } from './PathViewDetailedInfo';

interface Props {
  path: DetailedPath;
  updateData: () => Promise<void>;
}

const PathViewDetailedInfoSummary = ({ path, updateData }: Props) => {
  const { dateNow } = useDateNow();

  const { language, messages, serverConfig, theme, user } = useReduxState();

  return (
    <div className="summary">
      <Box>
        <div className="inner">
          <div className="picture">
            <PathPicture
              picture={path.picture}
              picturePosition={path.picturePosition}
              size={960}
            />
          </div>

          <div className="content">
            <div className="header">
              <div className="header-info">
                {messages.path}
                <span style={{ margin: '0 7px' }}>{'•'}</span>
                {getMessageWithPlaceholders({
                  message: messages.pathsNumberOfSteps,
                  placeholders: { numberOfSteps: path.steps?.length || 0 }
                })}
                <PublishedProductText
                  endDate={path.endDate}
                  startDate={path.startDate}
                  includePathTileDot={true}
                  useUserTexts={true}
                />
              </div>
            </div>

            <div className="title">{path.title}</div>

            {path.description ? (
              <div className="description">
                <RichTextValue html={path.description} />
              </div>
            ) : null}

            <div className="info">
              <DisplayAttributeInProductPage
                customAttributes={path.customAttributes}
                position="at start"
                productType="path"
              />

              {!serverConfig.productHideOwner && (
                <ContentLabel
                  horizontal={true}
                  label={messages.commonOwners}
                  spacing={20}
                >
                  <ContentValue>
                    <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                      {path.owners.map((owner) => (
                        <User
                          key={owner.id}
                          size="small"
                          showEmail={false}
                          showEmailTooltip={false}
                          titleStyle={{ fontSize: '1.4rem' }}
                          user={owner}
                        />
                      ))}
                    </FlexContainer>
                  </ContentValue>
                </ContentLabel>
              )}

              {path.categories?.length ? (
                <ContentLabel
                  horizontal={true}
                  label={messages.commonCategories}
                  spacing={20}
                >
                  <ContentValue>
                    <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                      {path.categories.map((category) => (
                        <Badge key={category} text={category} size="medium" />
                      ))}
                    </FlexContainer>
                  </ContentValue>
                </ContentLabel>
              ) : null}

              {path.competencies?.length ? (
                <ContentLabel
                  horizontal={true}
                  label={messages.competenciesGranted}
                  spacing={20}
                  style={{ whiteSpace: 'nowrap' }}
                  tooltip={messages.pathsCompetenciesGrantedInfo}
                >
                  <ContentValue>
                    <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                      {path.competencies.map((competency) => (
                        <CompetenciesProductBadge
                          key={competency.id}
                          competency={competency}
                        />
                      ))}
                    </FlexContainer>
                  </ContentValue>
                </ContentLabel>
              ) : null}

              {path.userIsEnrolled &&
              path.startDate &&
              Date.parse(path.startDate) < dateNow ? (
                <>
                  <ContentLabel
                    horizontal={true}
                    label={messages.commonStatus}
                    spacing={40}
                  >
                    <ContentValue>
                      {path.progressData?.pathCompletion ? (
                        <Badge
                          category="success-light"
                          text={messages.commonCompleted}
                        />
                      ) : (
                        <Badge
                          category="default"
                          text={
                            path.progressData?.stepsStarted
                              ? messages.commonInProgress
                              : messages.commonNotStarted
                          }
                        />
                      )}
                    </ContentValue>
                  </ContentLabel>

                  <ContentLabel
                    horizontal={true}
                    label={messages.commonProgress}
                    spacing={20}
                  >
                    <ContentValue>
                      <UserPathStepProgress progressData={path.progressData} />
                    </ContentValue>
                  </ContentLabel>

                  {path.progressData?.pathCompletion &&
                  path.certificateAllowDownloading ? (
                    <ContentLabel
                      horizontal={true}
                      label={messages.commonCertificate}
                      spacing={20}
                    >
                      <ContentValue>
                        <div className="certificate">
                          <ApiDownloadButton
                            category="lighter"
                            filename={`${path.title}.pdf`}
                            icon={['far', 'file-pdf']}
                            iconStyle={{
                              color: '#F40F02',
                              fontSize: '1.4rem',
                              top: 0
                            }}
                            size="small"
                            text={messages.completionsDownloadCertificate}
                            url={
                              `/admin/path-certificate` +
                              `?completionId=${path.progressData.pathCompletion.id}` +
                              `&language=${language}`
                            }
                          />
                        </div>
                      </ContentValue>
                    </ContentLabel>
                  ) : null}
                </>
              ) : null}

              <DisplayAttributeInProductPage
                customAttributes={path.customAttributes}
                productType="path"
              />
            </div>

            {!path.userIsEnrolled &&
            pathCanBeJoined({
              pathEndDate: path.endDate,
              user,
              userAccess: path.userAccess,
              userIsEnrolled: path.userIsEnrolled
            }) ? (
              <div className="join">
                <PathsTileJoinPath
                  isPathPage={true}
                  pathId={path.id}
                  pathTitle={path.title}
                  pathAvailable={Date.parse(path.startDate) <= dateNow}
                  updatePath={updateData}
                />
              </div>
            ) : null}
          </div>
        </div>
      </Box>

      <style jsx>{`
        .picture {
          border-radius: ${theme.cardBorderRadius};
          margin-bottom: 20px;
          overflow: hidden;
          position: relative;
          flex-grow: 0;
          flex-shrink: 0;
          height: 120px;
          width: 100%;
        }
        .inner {
          display: flex;
          flex-direction: column;
        }
        .content {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
        }
        .header {
          align-items: center;
          display: flex;
          gap: 8px;
        }
        .header-info {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          font-weight: 600;
        }
        .title {
          font-size: 2rem;
          font-weight: 700;
          margin-top: 5px;
        }
        .description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          line-height: 1.5;
          margin-top: 10px;
        }
        .info {
          margin-top: 30px;
        }
        .join {
          margin-top: 20px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .picture {
            height: 160px;
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .inner {
            flex-direction: row-reverse;
            gap: 30px;
            padding: 5px;
          }
          .picture {
            height: 200px;
            width: 400px;
            margin-bottom: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default PathViewDetailedInfoSummary;
